import { USER_ROLES } from 'constants/constants';

// Get current user from global data object
function getCurrentUser() {
    return data?.user || null;
}

// Get the user's highest role level for the current project
export function getCurrentProjectRoleLevel(projectId) {
    const currentUser = getCurrentUser();
    
    if (currentUser?.is_superuser) {
      return 999; // Superuser has all permissions
    }
    
    // Filter roles for the current project
    const projectRoles = currentUser?.roles?.filter(
      role => role.partner_gcp_project_id === projectId
    ) || [];
    
    if (projectRoles.length === 0) return 0;
    
    // Return highest role level for this project
    return Math.max(...projectRoles.map(role => role.level));
}

// Check if user has at least the specified role level for current project
export function hasMinimumRole(minimumLevel, projectId) {
    return getCurrentProjectRoleLevel(projectId) >= minimumLevel;
}

// Check if user has exactly the specified role level for current project
export function hasExactRole(exactLevel, projectId) {
    return getCurrentProjectRoleLevel(projectId) === exactLevel;
}

// EXACT ROLE CHECKS (exactly this role, not higher)
export function isViewer(projectId) {
    return hasExactRole(USER_ROLES.VIEWER.level, projectId);
}

export function isContributor(projectId) {
    return hasExactRole(USER_ROLES.CONTRIBUTOR.level, projectId);
}

export function isEditor(projectId) {
    return hasExactRole(USER_ROLES.EDITOR.level, projectId);
}

export function isEngineer(projectId) {
    return hasExactRole(USER_ROLES.ENGINEER.level, projectId);
}

export function isAdmin(projectId) {
    return hasExactRole(USER_ROLES.ADMIN.level, projectId);
}

// MINIMUM ROLE CHECKS (at least this role level or higher)
export function canView(projectId) {
    return hasMinimumRole(USER_ROLES.VIEWER.level, projectId);
}

export function canContribute(projectId) {
    return hasMinimumRole(USER_ROLES.CONTRIBUTOR.level, projectId);
}

export function canEdit(projectId) {
    return hasMinimumRole(USER_ROLES.EDITOR.level, projectId);
}

export function canEngineer(projectId) {
    return hasMinimumRole(USER_ROLES.ENGINEER.level, projectId);
}

export function canAdminister(projectId) {
    return hasMinimumRole(USER_ROLES.ADMIN.level, projectId);
}

// ============================================================================
// FIELD-LEVEL ROLE PERMISSIONS
// Defines the minimum role required to edit each field in edit mode.
// Fields not listed inherit the _default level for that form type.
// Fields hardcoded to editable={false} in form components are NOT affected.
// These restrictions apply to edit mode only, not when creating new records.
// ============================================================================

export const FIELD_PERMISSIONS = {
  job: {
    _default: USER_ROLES.ADMIN.level,
    proposal_link: USER_ROLES.CONTRIBUTOR.level,
    lessons_learned: USER_ROLES.CONTRIBUTOR.level,
    has_oversample: USER_ROLES.CONTRIBUTOR.level,
    release_status: USER_ROLES.CONTRIBUTOR.level,
    job_category: USER_ROLES.EDITOR.level,
  },
  session: {
    _default: USER_ROLES.ADMIN.level,
    fg_topic: USER_ROLES.CONTRIBUTOR.level,
    issue_type: USER_ROLES.CONTRIBUTOR.level,
  },
  client: {
    _default: USER_ROLES.EDITOR.level,
  },
  client_contact: {
    _default: USER_ROLES.CONTRIBUTOR.level,
  },
  facility: {
    _default: USER_ROLES.ADMIN.level,
  },
  vendor: {
    _default: USER_ROLES.ADMIN.level,
    notes: USER_ROLES.CONTRIBUTOR.level,
  },
  analyst: {
    _default: USER_ROLES.ADMIN.level,
  },
};

export const ASSOCIATION_EDIT_PERMISSIONS = {
  job: {
    _default: USER_ROLES.ADMIN.level,
    job_analysts: USER_ROLES.ADMIN.level,
    clients: USER_ROLES.EDITOR.level,
  },
  session: {
    _default: USER_ROLES.ADMIN.level,
  },
  client: {
    _default: USER_ROLES.EDITOR.level,
    client_contacts: USER_ROLES.CONTRIBUTOR.level,
  },
  client_contacts: {
    _default: USER_ROLES.CONTRIBUTOR.level,
  },
  analyst: {
    _default: USER_ROLES.ADMIN.level,
  },
};

// Minimum role required to CREATE a new record of each type.
// Derived from: can the user fill in all required fields for that form?
export const CREATION_ROLES = {
  jobs: USER_ROLES.ADMIN.level,
  sessions: USER_ROLES.ADMIN.level,
  clients: USER_ROLES.EDITOR.level,
  client_contacts: USER_ROLES.CONTRIBUTOR.level,
  vendors: USER_ROLES.ADMIN.level,
  facilities: USER_ROLES.ADMIN.level,
  analysts: USER_ROLES.ADMIN.level,
};

/**
 * Check if the current user can create a new record of the given type.
 */
export function canCreateRecordType(tableType, projectId) {
  const requiredLevel = CREATION_ROLES[tableType] ?? USER_ROLES.ADMIN.level;
  return hasMinimumRole(requiredLevel, projectId);
}

/**
 * Check if the current user can edit a specific field on a form.
 * Returns true if the user's role meets the minimum level for that field.
 */
export function canEditField(fieldName, formType, projectId) {
  const permissions = FIELD_PERMISSIONS[formType];
  if (!permissions) return canContribute(projectId);

  const requiredLevel = permissions[fieldName] ?? permissions._default ?? USER_ROLES.ADMIN.level;
  return hasMinimumRole(requiredLevel, projectId);
}

/**
 * Check if the current user can edit a specific association accordion.
 */
export function canEditAssociation(associationKey, parentType, projectId) {
  const permissions = ASSOCIATION_EDIT_PERMISSIONS[parentType];
  if (!permissions) return canContribute(projectId);

  const requiredLevel = permissions[associationKey] ?? permissions._default ?? USER_ROLES.ADMIN.level;
  return hasMinimumRole(requiredLevel, projectId);
}

// NAVIGATION HELPERS
export function navigateBack(navigate, fallbackPath) {
  if (window.history.length > 1) {
    navigate(-1);
  } else if (fallbackPath) {
    navigate(fallbackPath);
  }
}

// DATA HELPER
export function cleanFormData(data, nullableFields = [], phoneFields = [], integerFields = []) {
  const cleanedData = { ...data };

  nullableFields.forEach(field => {
    if (cleanedData[field] === '') {
      cleanedData[field] = null;
    }
  });

  phoneFields.forEach(field => {
    if (cleanedData[field]) {
      const formatted = formatPhoneNumber(cleanedData[field]);
      cleanedData[field] = formatted;
    }
  });

  integerFields.forEach(field => {
    if (cleanedData[field] !== null && cleanedData[field] !== undefined && cleanedData[field] !== '') {
      cleanedData[field] = parseInt(cleanedData[field], 10);
    }
  });

  return cleanedData;
}

export function formatPhoneNumber(phoneNumber) {
  if (!phoneNumber || phoneNumber.trim() === '') {
    return null;
  }

  const digitsOnly = phoneNumber.replace(/\D/g, '');

  if (digitsOnly.length === 0) {
    return null;
  }

  if (digitsOnly.length === 10) {
    return `+1${digitsOnly}`;
  } else if (digitsOnly.length === 11 && digitsOnly.startsWith('1')) {
    return `+${digitsOnly}`;
  } else if (digitsOnly.length > 10) {
    return `+${digitsOnly}`;
  }

  return null;
}

export function validatePhoneNumber(phoneNumber) {
  if (!phoneNumber || phoneNumber.trim() === '') {
    return null;
  }

  const digitsOnly = phoneNumber.replace(/\D/g, '');

  if (digitsOnly.length < 10) {
    return 'Phone number must be at least 10 digits';
  }

  if (digitsOnly.length > 15) {
    return 'Phone number is too long';
  }

  return null;
}

// API HELPER FUNCTIONS
import { getCsrfToken } from 'utils/csrf';

/**
 * Fetch a single record by ID
 */
export async function fetchRecord(apiEndpoint, id) {
  const response = await fetch(`${apiEndpoint}/${id}`, {
    credentials: 'include',
  });
  
  if (!response.ok) {
    // Try to get error message from response
    let errorMessage = `Failed to fetch record (${response.status})`;
    try {
      const errorData = await response.json();
      errorMessage = errorData.message || errorData.error || errorMessage;
    } catch {
      // If response isn't JSON, it's probably HTML (error page)
      errorMessage = `Server error: ${response.status} ${response.statusText}`;
    }
    throw new Error(errorMessage);
  }
  
  // Check if response is actually JSON before parsing
  const contentType = response.headers.get('content-type');
  if (!contentType || !contentType.includes('application/json')) {
    throw new Error('Server returned non-JSON response');
  }
  
  return await response.json();
}

/**
 * Update a record
 */
export async function updateRecord(apiEndpoint, id, data) {
  const response = await fetch(`${apiEndpoint}/${id}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRFToken': getCsrfToken(),
    },
    body: JSON.stringify(data),
    credentials: 'include',
  });
  
  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(JSON.stringify(errorData) || 'Failed to update');
  }
  
  return await response.json();
}

/**
 * Create a new record
 */
export async function createRecord(apiEndpoint, data) {
  const response = await fetch(apiEndpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRFToken': getCsrfToken(),
    },
    body: JSON.stringify(data),
    credentials: 'include',
  });
  
  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(JSON.stringify(errorData) || 'Failed to create');
  }
  
  return await response.json();
}

/**
 * Delete a record
 */
export async function deleteRecord(apiEndpoint, id) {
  const response = await fetch(`${apiEndpoint}/${id}`, {
    method: 'DELETE',
    headers: {
      'X-CSRFToken': getCsrfToken(),
    },
    credentials: 'include',
  });
  
  if (!response.ok) {
    throw new Error('Failed to delete record');
  }
}

// ============================================================================
// DATE UTILITIES
// ============================================================================

// Formats a date string to MM/DD/YYYY format (date only, no time)
export const formatDateOnly = (dateString) => {
  if (!dateString) return '-';
  let date;
  if (typeof dateString === 'string' && !dateString.includes('T')) {
    const [year, month, day] = dateString.split('-').map(Number);
    date = new Date(year, month - 1, day);
  } else {
    date = new Date(dateString);
  }
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
};

// Formats date range as "MM/DD/YYYY - MM/DD/YYYY"
export const formatDateRange = (startDate, endDate) => {
  const start = startDate ? formatDateOnly(startDate) : '-';
  const end = endDate ? formatDateOnly(endDate) : '-';
  if (start === '-' && end === '-') return '-';
  return `${start} - ${end}`;
};

// Helper function to normalize dates for comparison
// Returns date in YYYY-MM-DD format, converted to Eastern Time
export const normalizeDateString = (dateValue) => {
  if (!dateValue) return null;
  if (typeof dateValue === 'string' && dateValue.includes('T')) {
    // DateTime string — convert to Eastern Time calendar date
    const d = new Date(dateValue);
    if (isNaN(d.getTime())) return dateValue.split('T')[0];

    // Convert to Eastern Time
    const etDateString = d.toLocaleString('en-US', {
      timeZone: 'America/New_York',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });

    // Parse MM/DD/YYYY to YYYY-MM-DD
    const [month, day, year] = etDateString.split('/');
    return `${year}-${month}-${day}`;
  }
  // Plain date string (YYYY-MM-DD) — use as-is
  return typeof dateValue === 'string' ? dateValue : String(dateValue);
};

// ============================================================================
// STATUS UTILITIES - Reusable functions for handling status across all Hart entities
// ============================================================================

// Status color mapping - THE ONLY HARDCODED THING
// Maps known status values to their colors
// Any status not in this map will default to grey
// Statuses themselves come from the database via admin panel dropdown management
export const STATUS_COLOR_MAP = {
  'Active': 'success.main',
  'Inactive': 'grey.500',
  'On Hold': 'warning.main',
  'Completed': 'info.main',
  'Cancelled': 'error.main',
};

// Table type display names (for "Include All X" labels)
export const TABLE_DISPLAY_NAMES = {
  jobs: 'Jobs',
  analysts: 'Analysts',
  vendors: 'Vendors',
  clients: 'Clients',
  facilities: 'Facilities',
};

/**
 * Get the toggle filter label dynamically for any table type
 * Returns "Include All Jobs", "Include All Vendors", etc.
 */
export const getIncludeAllLabel = (tableType) => {
  const displayName = TABLE_DISPLAY_NAMES[tableType] || tableType;
  return `Include All ${displayName}`;
};

/**
 * Get the status color based on status value
 * Looks up color in STATUS_COLOR_MAP, defaults to grey for unknown statuses
 * This allows new statuses to be added via admin panel without code changes
 */
export const getStatusColor = (status) => {
  if (!status) return 'grey.500';
  const statusStr = String(status);
  
  // Try exact match first (faster)
  if (STATUS_COLOR_MAP[statusStr]) {
    return STATUS_COLOR_MAP[statusStr];
  }
  
  // Fallback to case-insensitive match
  const statusKey = Object.keys(STATUS_COLOR_MAP).find(
    key => key.toLowerCase() === statusStr.toLowerCase()
  );
  
  return statusKey ? STATUS_COLOR_MAP[statusKey] : 'grey.500';
};

/**
 * Get the status label based on status value
 * Returns the status as-is (labels come from database)
 */
export const getStatusLabel = (status) => {
  if (!status) return status;
  return String(status);
};